import { ActionFunction, redirect, replace } from 'react-router-dom';
import { client } from '~/api/client';
import { mutateData } from '~/api/data/request';
import { submitMutation } from '~/api/data/responses';
import {
  CancelBusinessPolicyDocument,
  CancelBusinessPolicyMutation,
  CancelBusinessPolicyMutationVariables,
  CreateBusinessPoliciesDocument,
  CreateBusinessPoliciesMutation,
  CreateBusinessPoliciesMutationVariables,
  DeleteBusinessPolicyDocument,
  DeleteBusinessPolicyMutation,
  DeleteBusinessPolicyMutationVariables,
  DeleteFileDocument,
  DeleteFileMutation,
  DeleteFileMutationVariables,
  FileType,
  ReinstateBusinessPolicyDocument,
  ReinstateBusinessPolicyMutation,
  ReinstateBusinessPolicyMutationVariables,
  RenewBusinessPolicyDocument,
  RenewBusinessPolicyMutation,
  RenewBusinessPolicyMutationVariables,
  UpdateBusinessPolicyDocument,
  UpdateBusinessPolicyMutation,
  UpdateBusinessPolicyMutationVariables,
  UploadPolicyFileDocument,
  UploadPolicyFileMutation,
  UploadPolicyFileMutationVariables
} from '~/api/graphql';

export const createBusinessPolicy = (variables: CreateBusinessPoliciesMutationVariables) =>
  mutateData<CreateBusinessPoliciesMutation, CreateBusinessPoliciesMutationVariables>(client, {
    mutation: CreateBusinessPoliciesDocument,
    variables,
    errorPolicy: 'all'
  });

export const updateBusinessPolicy = (variables: UpdateBusinessPolicyMutationVariables) =>
  mutateData<UpdateBusinessPolicyMutation, UpdateBusinessPolicyMutationVariables>(client, {
    mutation: UpdateBusinessPolicyDocument,
    variables
  });

export const deleteBusinessPolicy = (variables: DeleteBusinessPolicyMutationVariables) =>
  mutateData<DeleteBusinessPolicyMutation, DeleteBusinessPolicyMutationVariables>(client, {
    mutation: DeleteBusinessPolicyDocument,
    variables
  });

export const cancelBusinessPolicy = (variables: CancelBusinessPolicyMutationVariables) =>
  mutateData<CancelBusinessPolicyMutation, CancelBusinessPolicyMutationVariables>(client, {
    mutation: CancelBusinessPolicyDocument,
    variables
  });

export const renewBusinessPolicy = (variables: RenewBusinessPolicyMutationVariables) =>
  mutateData<RenewBusinessPolicyMutation, RenewBusinessPolicyMutationVariables>(client, {
    mutation: RenewBusinessPolicyDocument,
    variables
  });

export const reinstateBusinessPolicy = (variables: ReinstateBusinessPolicyMutationVariables) =>
  mutateData<ReinstateBusinessPolicyMutation, ReinstateBusinessPolicyMutationVariables>(client, {
    mutation: ReinstateBusinessPolicyDocument,
    variables
  });

export const uploadPolicyFile = (variables: UploadPolicyFileMutationVariables) =>
  mutateData<UploadPolicyFileMutation, UploadPolicyFileMutationVariables>(client, {
    mutation: UploadPolicyFileDocument,
    variables
  });

export const deleteFile = (variables: DeleteFileMutationVariables) =>
  mutateData<DeleteFileMutation, DeleteFileMutationVariables>(client, {
    mutation: DeleteFileDocument,
    variables
  });

export const submitCreateBusinessPolicy: ActionFunction = async ({ request }) =>
  submitMutation(createBusinessPolicy({ input: await request.json() }), async (data) =>
    redirect(`/app/policies/${data?.createBusinessPolicies?.[0]?.id}/overview`)
  );

export const submitDeleteBusinessPolicy: ActionFunction = async ({ params }) =>
  submitMutation(deleteBusinessPolicy({ id: params.id || '' }), async () =>
    replace(`/app/policies`)
  );

export const submitCancelBusinessPolicy: ActionFunction = async ({ params, request }) =>
  submitMutation(
    cancelBusinessPolicy({ id: params.id || '', input: await request.json() }),
    async (data) => replace(`/app/policies/${data?.cancelBusinessPolicy?.id}/overview`)
  );

export const submitRenewBusinessPolicy: ActionFunction = async ({ params, request }) =>
  submitMutation(
    renewBusinessPolicy({ id: params.id || '', input: await request.json() }),
    async (data) => replace(`/app/accounts/${data?.renewBusinessPolicy?.account.id}/policies`)
  );

export const submitReinstateBusinessPolicy: ActionFunction = async ({ params, request }) =>
  submitMutation(
    reinstateBusinessPolicy({ id: params.id || '', input: await request.json() }),
    async (data) => redirect(`/app/policies/${data?.reinstateBusinessPolicy?.id}/overview`)
  );

export const submitUpdateBusinessPolicy: ActionFunction = async ({ params, request }) =>
  submitMutation(
    updateBusinessPolicy({ id: params.id || '', input: await request.json() }),
    async (data) => redirect(`/app/policies/${data?.updateBusinessPolicy?.id}/overview`)
  );

export const submitDeleteFile: ActionFunction = async ({ request }) =>
  submitMutation(deleteFile({ fileId: (await request.json()).fileId }));

export const submitUploadPolicyFile: ActionFunction = async ({ params, request }) => {
  const formData = await request.formData();
  return submitMutation(
    uploadPolicyFile({
      policyId: params.id || '',
      file: {
        notes: formData.get('notes') as string,
        type: formData.get('type') as FileType,
        visible: formData.get('visible') === 'true',
        file: formData.get('file') as File
      }
    })
  );
};
